import { useState } from 'react'

import { parallaxReading, ru } from '../../lib/physics'
import { Predict, Slider } from './common'

/**
 * Глаз на уровне воды (урок 10): шкала наклеена снаружи, а вода — за стенкой. Смотришь сверху —
 * край воды проецируется на шкалу выше, снизу — ниже. Правильный отсчёт — только когда глаз
 * на уровне поверхности воды (так в уроке ставили камеру).
 */

const УРОВЕНЬ = 100 // мл — сколько налито на самом деле
const PX_PER_ML = 1.4
const ДНО = 230
const D = 120 // px от глаза до шкалы
const G = 40 // px: вода «за» шкалой — толщина стакана до дальнего края мениска

export default function ParallaxLab() {
  const [глаз, setГлаз] = useState(140) // мл — на какой высоте глаз, в делениях шкалы

  const отсчёт = parallaxReading(УРОВЕНЬ, глаз, D, G)
  const y = (v: number) => ДНО - v * PX_PER_ML
  const xШкала = 200
  const xГлаз = xШкала + D
  const xВода = xШкала - G

  return (
    <div className="card">
      <svg className="lab-svg" style={{ maxWidth: 380, margin: '0 auto' }} viewBox="0 0 340 250" role="img" aria-label="Глаз смотрит на шкалу мензурки">
        <rect x={xВода - 60} y={y(УРОВЕНЬ)} width={60 + G} height={ДНО - y(УРОВЕНЬ)} className="fill-earth" />
        <rect x={xВода - 60} y={y(150)} width={60 + G} height={ДНО - y(150)} className="ink" fill="none" strokeWidth={2} />
        {Array.from({ length: 16 }, (_, i) => (
          <g key={i}>
            <line x1={xШкала} y1={y(i * 10)} x2={xШкала + (i % 5 ? 6 : 12)} y2={y(i * 10)} className="ink" strokeWidth={0.8} />
            {i % 5 === 0 && <text x={xШкала + 14} y={y(i * 10) + 4} className="lab-tick">{i * 10}</text>}
          </g>
        ))}
        {/* луч от глаза к краю воды за стенкой */}
        <line x1={xГлаз} y1={y(глаз)} x2={xВода} y2={y(УРОВЕНЬ)} className="ink-heavy" strokeWidth={1.4} strokeDasharray="5 3" />
        <circle cx={xШкала} cy={y(отсчёт)} r={4} className="fill-heavy" />
        <ellipse cx={xГлаз} cy={y(глаз)} rx={11} ry={7} className="fill-paper ink" strokeWidth={1.5} />
        <circle cx={xГлаз - 3} cy={y(глаз)} r={3} className="fill-ink" />
      </svg>

      <div className="lab-readout">
        <span>налито на самом деле: {УРОВЕНЬ} мл</span>
        <span className="heavy">глаз видит: {ru(отсчёт, 0)} мл</span>
        <span>ошибка: {ru(отсчёт - УРОВЕНЬ, 0)} мл</span>
      </div>

      <Slider label="Высота глаза" value={глаз} min={40} max={160} step={5} unit="" shown={глаз > УРОВЕНЬ ? 'выше воды' : глаз < УРОВЕНЬ ? 'ниже воды' : 'на уровне воды'} onChange={setГлаз} />

      <Predict
        question="Смотришь на мензурку сверху, глаз выше воды. Отсчёт выйдет…"
        options={['больше, чем налито', 'точным', 'меньше, чем налито']}
        right={0}
        revealed={глаз >= 140}
        explain="Луч от глаза к краю воды пересекает шкалу выше уровня воды — отсчёт завышен. Глаз на уровне воды — ошибки нет."
      />
      <p className="muted small">
        Эта ошибка называется параллаксом. Перед отсчётом мензурку ставят на стол, а глаз опускают
        до уровня воды и читают по нижнему краю мениска.
      </p>
    </div>
  )
}
